import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/LanguageContext";

export default function RetentionNotices({ compact = false }) {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [error, setError] = useState(false);
  const [next, setNext] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const enabled = process.env.REACT_APP_RESIDUAL_NOTICES_ENABLED === "1";
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api.get("/account/retention-notices").then(r => {
      if (!cancelled) { setItems(r.data.items || []); setNext(r.data.next_cursor || null); setUnavailable(!!r.data.has_unavailable); }
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [enabled]);
  const loadMore = async () => {
    if (!next || loading) return;
    setLoading(true); setError(false);
    try {
      const r = await api.get("/account/retention-notices", { params: { after: next } });
      setItems(rows => [...new Map([...rows, ...(r.data.items || [])].map(item => [item.id, item])).values()]);
      setNext(r.data.next_cursor || null); setUnavailable(value => value || !!r.data.has_unavailable);
    } catch { setError(true); }
    finally { setLoading(false); }
  };
  if (!enabled) return null;
  const en = lang === "en";
  const acknowledge = async item => {
    setBusy(item.id); setError(false);
    try {
      await api.post(`/account/retention-notices/${item.id}/acknowledge`, { content_sha256: item.content_sha256 });
      setItems(rows => rows.map(row => row.id === item.id ? { ...row, acknowledged: true } : row));
    } catch { setError(true); }
    finally { setBusy(null); }
  };
  if (compact) {
    const unread = items.filter(item => !item.acknowledged);
    if (!error && !unavailable && !next && unread.length === 0) return null;
    return <aside className="nvl-card p-4 mb-4" aria-label={en ? "Retention notices" : "Avvisi di conservazione"}>
      <a className="underline" href="/app/settings/privacy">
        {(error || unavailable) ? (en ? "Check your data retention notices" : "Controlla gli avvisi sui tuoi dati")
          : (en ? "You have a notice about the expiry of messages. View it and export your data before the deadline."
            : "Hai un avviso sulla scadenza dei messaggi. Leggilo ed esporta i tuoi dati prima della scadenza.")}
      </a>
    </aside>;
  }
  return <section aria-label={en ? "Retention notices" : "Avvisi di conservazione"}>
    {error && <p role="alert">{en ? "Unable to load or confirm the notice. Please reload and try again." : "Impossibile caricare o confermare l’avviso. Ricarica la pagina e riprova."}</p>}
    {unavailable && <p role="alert">{en ? "Some notices could not be displayed. Contact info@gd8.it for assistance." : "Non è stato possibile mostrare alcuni avvisi. Contatta info@gd8.it per assistenza."}</p>}
    {items.map(item => <article className="nvl-card p-6 mt-4" key={item.id}>
      <h2 className="text-xl">{item.payload.title}</h2>
      <p className="mt-3">{item.payload.body}</p>
      <a className="underline block mt-3" href="/app/settings/privacy">{item.payload.cta}</a>
      <button className="nvl-btn nvl-btn-outline mt-3" disabled={item.acknowledged || busy !== null}
        onClick={() => acknowledge(item)}>
        {item.acknowledged ? (en ? "Acknowledged" : "Presa visione registrata") : (en ? "I have read this notice" : "Ho letto questo avviso")}
      </button>
    </article>)}
    {next && <button className="nvl-btn nvl-btn-outline mt-3" disabled={loading} onClick={loadMore}>
      {loading ? (en ? "Loading…" : "Caricamento…") : (en ? "Load more notices" : "Carica altri avvisi")}
    </button>}
  </section>;
}
